import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { useState, useEffect } from "react";
import { translations } from "../translations";

const FeatureBox = ({ icon, titleKey, descriptionKey }) => {
  const { t } = useLanguage();
  return (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="bg-white rounded-xl p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center shadow-lg">
        <img
          src={icon}
          alt={t(titleKey)}
          className="w-12 h-12 brightness-0"
        />
      </div>
      <h3 className="text-xl font-semibold mb-2 text-white">{t(titleKey)}</h3>
      <p className="text-gray-400 text-sm">{t(descriptionKey)}</p>
    </motion.div>
  );
};

const PriceCard = ({ price, title, description }) => (
  <motion.div
    className="card-gradient p-8"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
  >
    <h3 className="text-4xl font-bold mb-4">{price}</h3>
    <h4 className="text-xl font-semibold mb-3">{title}</h4>
    <p className="leading-relaxed">{description}</p>
  </motion.div>
);

const SupportCard = ({ icon, title, description }) => (
  <motion.div
    className="card-gradient text-center p-8"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
  >
    <div className="text-white text-4xl font-bold mb-4">{icon}</div>
    <h4 className="text-xl font-semibold mb-3">{title}</h4>
    <p className="leading-relaxed">{description}</p>
  </motion.div>
);

const Hero = () => {
  const { t, language } = useLanguage();
  const [isLoaded, setIsLoaded] = useState(false);

  // Optimized scroll handling with throttling
  const [scrollSpeed, setScrollSpeed] = useState(30);

  useEffect(() => {
    // Delay heavy animations until component is mounted
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Throttled scroll handler to reduce performance impact
  useEffect(() => {
    let lastScrollY = 0;
    let ticking = false;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollDelta = Math.abs(scrollY - lastScrollY);

      // Simplified speed calculation
      if (scrollDelta > 20) {
        setScrollSpeed(15);
      } else if (scrollDelta > 10) {
        setScrollSpeed(22);
      } else {
        setScrollSpeed(30);
      }

      lastScrollY = scrollY;
      ticking = false;
    };

    const requestScrollUpdate = () => {
      if (!ticking) {
        requestAnimationFrame(handleScroll);
        ticking = true;
      }
    };

    // Only add scroll listener after component is loaded
    if (isLoaded) {
      window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    }

    return () => window.removeEventListener('scroll', requestScrollUpdate);
  }, [isLoaded]);

  return (
    <div className="overflow-x-hidden w-full">
      {/* Main Hero Section */}
      <section className="relative min-h-screen pt-20 pb-20 md:pb-12 overflow-hidden w-full bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="container mx-auto max-w-screen-xl px-6 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 min-h-[80vh] items-center">
            {/* Left Content - Modern Asymmetric Layout */}
            <div className="lg:col-span-7 text-center lg:text-left space-y-8 order-2 lg:order-1">
              <div className="space-y-6">
                <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
                  <span className="relative z-10">
                    {t("hero.title")}
                  </span>
                </h1>

                <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto lg:mx-0">
                  Platform trading terpercaya dengan teknologi terdepan untuk kesuksesan finansial
                  Anda.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <motion.a
                  href="https://stockity.id/auth?a=e4dd5e5e53f7&t=0#signup"
                  className="bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 text-white px-8 py-3 rounded-full text-lg font-medium hover:brightness-110 transition-all inline-block shadow-[0_10px_30px_rgba(56,189,248,0.4)]"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t("hero.demo")} →
                </motion.a>
              </div>

              {/* Modern Stats Layout */}
              <div className="grid grid-cols-3 gap-4 pt-8 border-t border-gray-800/50">
                {[
                  { label: "Users", value: "100K+" },
                  { label: "Countries", value: "50+" },
                  { label: "Uptime", value: "99.9%" }
                ].map((stat, idx) => (
                  <motion.div
                    key={stat.label}
                    className="text-center"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 uppercase tracking-wider">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right Content - Phone Image with Modern Positioning */}
            <div className="lg:col-span-5 relative order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-[240px] sm:max-w-[300px] lg:max-w-[350px] mx-auto lg:mx-0">
                <img
                  src="/phone.png"
                  alt="Trading App"
                  className="w-full drop-shadow-2xl relative z-10"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-blue-800 via-cyan-400 to-blue-800 w-full">
        <div className="container mx-auto max-w-7xl px-6">
          {/* Centered Header Layout */}
          <div className="text-center mb-16">
            <motion.span
              className="text-yellow-400 font-semibold mb-4 block tracking-wider text-sm uppercase"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              METODE PEMBAYARAN
            </motion.span>
            <motion.h2
              className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              {t("sections.easyDeposits")}
            </motion.h2>
            <motion.p
              className="text-lg md:text-xl text-cyan-50/90 leading-relaxed max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {t("sections.choosePayment")}
            </motion.p>
          </div>

          {/* Payment Logos Slider - No Background */}
          <motion.div
            className="max-w-7xl mx-auto overflow-hidden w-full"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            {/* Premium Payment Logos Slider */}
            <div className="payment-slider-container relative">
              <motion.div
                className="flex items-center space-x-12 payment-slider"
                animate={{ x: [0, -2400] }}
                transition={{
                  duration: scrollSpeed,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                {/* First Set of Logos */}
                <div className="flex items-center space-x-12 flex-shrink-0">
                  {/* Crypto Logos */}
                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/btc_svg.svg"
                      alt="Bitcoin"
                      className="h-16 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/eth_logo.svg"
                      alt="Ethereum"
                      className="h-16 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/usdt_logo.svg"
                      alt="USDT"
                      className="h-16 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  {/* Bank Logos */}
                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: -3 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/bca_logo.svg"
                      alt="BCA"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: 3 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/mandiri_logo.svg"
                      alt="Mandiri"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/bni_logo.svg"
                      alt="BNI"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/bri_logo.svg"
                      alt="BRI"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: -3 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/permata_logo.svg"
                      alt="Permata"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  {/* E-Wallet Logos */}
                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: 3 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/dana_logo.svg"
                      alt="DANA"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/ovo_logo.svg"
                      alt="OVO"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>

                  <motion.div
                    className="payment-logo-item group"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="/logo/spey_logo.svg"
                      alt="ShopeePay"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                </div>

                {/* Second Set of Logos (Duplicate for seamless loop) */}
                <div className="flex items-center space-x-12 flex-shrink-0">
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: 5 }}>
                    <img
                      src="/logo/btc_svg.svg"
                      alt="Bitcoin"
                      className="h-16 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: -5 }}>
                    <img
                      src="/logo/eth_logo.svg"
                      alt="Ethereum"
                      className="h-16 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: 5 }}>
                    <img
                      src="/logo/usdt_logo.svg"
                      alt="USDT"
                      className="h-16 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: -3 }}>
                    <img
                      src="/logo/bca_logo.svg"
                      alt="BCA"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: 3 }}>
                    <img
                      src="/logo/mandiri_logo.svg"
                      alt="Mandiri"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: -5 }}>
                    <img
                      src="/logo/bni_logo.svg"
                      alt="BNI"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: 5 }}>
                    <img
                      src="/logo/bri_logo.svg"
                      alt="BRI"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: -3 }}>
                    <img
                      src="/logo/permata_logo.svg"
                      alt="Permata"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: 3 }}>
                    <img
                      src="/logo/dana_logo.svg"
                      alt="DANA"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: -5 }}>
                    <img
                      src="/logo/ovo_logo.svg"
                      alt="OVO"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                  <motion.div className="payment-logo-item group" whileHover={{ scale: 1.1, rotate: 5 }}>
                    <img
                      src="/logo/spey_logo.svg"
                      alt="ShopeePay"
                      className="h-12 w-auto drop-shadow-lg transition-all duration-300 opacity-80 group-hover:opacity-100"
                    />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Centered CTA Layout */}
          <motion.div
            className="flex justify-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <motion.a
              href="https://stockity.id/auth?a=e4dd5e5e53f7&t=0#signup"
              className="bg-gradient-to-br from-black/90 via-gray-900/80 to-black/90 text-white px-8 py-3 rounded-full text-lg font-medium hover:brightness-110 transition-all inline-block shadow-[0_10px_30px_rgba(56,189,248,0.4)]"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t("sections.createAccount")}
            </motion.a>
          </motion.div>
        </div>

        {/* Subtle Premium Glow Effects - Blue/Cyan */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute top-16 left-16 w-72 h-72 bg-cyan-300/35 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.25, 0.55, 0.25]
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-16 right-16 w-80 h-80 bg-blue-500/35 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 0.8, 1.2],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-sky-400/30 rounded-full blur-3xl"
            animate={{
              scale: [0.7, 1.4, 0.7],
              opacity: [0.15, 0.4, 0.15]
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
        </div>
      </section>

      {/* Platform Features Section */}
      <section className="relative py-20 overflow-hidden bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 w-full">
        <div className="container mx-auto max-w-7xl px-6 py-20 relative z-10 w-full">
          {/* Modern Split Header Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <motion.span
                className="text-blue-400 font-semibold mb-4 block tracking-wider text-sm uppercase"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                KEUNGGULAN PLATFORM
              </motion.span>
              <motion.h2
                className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
              >
                Mengapa Memilih{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-200 to-blue-400">
                  Stockity?
                </span>
              </motion.h2>
            </div>
            <div className="lg:col-span-7 flex items-center">
              <motion.p
                className="text-lg md:text-xl text-gray-400 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Nikmati pengalaman trading terbaik dengan fitur-fitur premium yang dirancang untuk
                kesuksesan finansial Anda.
              </motion.p>
            </div>
          </div>

          {/* Modern Staggered Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Global Reach Card */}
            <motion.div
              className="relative group cursor-pointer"
              initial={{ opacity: 0, y: 60, rotateX: 30 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 1, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -10,
                rotateY: 8,
                rotateX: -5,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <div className="bg-gradient-to-br from-black/90 via-gray-900/80 to-black/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-800/50 shadow-2xl relative overflow-hidden h-full">
                {/* Enhanced background effects */}
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800/5 via-transparent to-gray-900/5" />
                <motion.div
                  className="absolute top-4 right-4 w-64 h-64 bg-gradient-to-br from-gray-700/10 to-gray-900/10 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />

                {/* Icon with enhanced animation */}
                <motion.div
                  className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center shadow-xl relative z-10 border border-gray-700/50"
                  whileHover={{
                    rotateY: 360,
                    scale: 1.1,
                    transition: { duration: 0.8, ease: "easeInOut" }
                  }}
                  animate={{
                    y: [-2, 2, -2]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <img
                    src="/global.png"
                    alt="Global Reach"
                    className="w-8 h-8 brightness-0 invert"
                  />
                </motion.div>

                <div className="text-center relative z-10">
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-300 transition-colors duration-500">
                    {t("hero.features.globalReach.title")}
                  </h3>
                  <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                    {t("hero.features.globalReach.description")}
                  </p>
                </div>

                {/* Enhanced floating particles */}
                <motion.div
                  className="absolute top-6 right-6 w-2 h-2 bg-gray-500/60 rounded-full"
                  animate={{
                    y: [-8, 8, -8],
                    opacity: [0.4, 1, 0.4],
                    scale: [0.8, 1.2, 0.8]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                <motion.div
                  className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-gray-400/60 rounded-full"
                  animate={{
                    y: [6, -6, 6],
                    opacity: [0.5, 1, 0.5],
                    scale: [1, 0.8, 1]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />
              </div>
            </motion.div>

            {/* Simplicity Card - Staggered Position */}
            <motion.div
              className="relative group cursor-pointer md:mt-8"
              initial={{ opacity: 0, y: 60, rotateX: 30 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -10,
                rotateY: -8,
                rotateX: -5,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <div className="bg-gradient-to-br from-black/90 via-gray-900/80 to-black/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-800/50 shadow-2xl relative overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800/5 via-transparent to-gray-900/5" />
                <motion.div
                  className="absolute top-4 right-4 w-64 h-64 bg-gradient-to-br from-gray-700/10 to-gray-900/10 rounded-full blur-3xl"
                  animate={{
                    scale: [1.2, 1, 1.2],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                />

                <motion.div
                  className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center shadow-xl relative z-10 border border-gray-700/50"
                  whileHover={{
                    rotateY: 360,
                    scale: 1.1,
                    transition: { duration: 0.8, ease: "easeInOut" }
                  }}
                  animate={{
                    y: [-2, 2, -2]
                  }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5
                  }}
                >
                  <img
                    src="/simple.png"
                    alt="Simplicity"
                    className="w-8 h-8 brightness-0 invert"
                  />
                </motion.div>

                <div className="text-center relative z-10">
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-300 transition-colors duration-500">
                    {t("hero.features.simplicity.title")}
                  </h3>
                  <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                    {t("hero.features.simplicity.description")}
                  </p>
                </div>

                <motion.div
                  className="absolute top-6 right-6 w-2 h-2 bg-gray-500/60 rounded-full"
                  animate={{
                    y: [8, -8, 8],
                    opacity: [0.4, 1, 0.4]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.5
                  }}
                />
              </div>
            </motion.div>

            {/* Flexibility Card - Staggered Position */}
            <motion.div
              className="relative group cursor-pointer lg:mt-16"
              initial={{ opacity: 0, y: 60, rotateX: 30 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -10,
                rotateY: 8,
                rotateX: -5,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <div className="bg-gradient-to-br from-black/90 via-gray-900/80 to-black/90 backdrop-blur-xl rounded-3xl p-8 border border-gray-800/50 shadow-2xl relative overflow-hidden h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-800/5 via-transparent to-gray-900/5" />
                <motion.div
                  className="absolute top-4 right-4 w-64 h-64 bg-gradient-to-br from-gray-700/10 to-gray-900/10 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.4, 1],
                    opacity: [0.2, 0.4, 0.2]
                  }}
                  transition={{
                    duration: 3.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 2
                  }}
                />

                <motion.div
                  className="bg-gradient-to-br from-gray-700 to-gray-900 rounded-2xl p-4 w-16 h-16 mx-auto mb-6 flex items-center justify-center shadow-xl relative z-10 border border-gray-700/50"
                  whileHover={{
                    rotateY: 360,
                    scale: 1.1,
                    transition: { duration: 0.8, ease: "easeInOut" }
                  }}
                  animate={{
                    y: [-2, 2, -2]
                  }}
                  transition={{
                    duration: 2.8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1
                  }}
                >
                  <img
                    src="/flexible.png"
                    alt="Flexibility"
                    className="w-8 h-8 brightness-0 invert"
                  />
                </motion.div>

                <div className="text-center relative z-10">
                  <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-gray-300 transition-colors duration-500">
                    {t("hero.features.flexibility.title")}
                  </h3>
                  <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-500">
                    {t("hero.features.flexibility.description")}
                  </p>
                </div>

                <motion.div
                  className="absolute top-6 right-6 w-2 h-2 bg-gray-500/60 rounded-full"
                  animate={{
                    y: [-6, 10, -6],
                    opacity: [0.4, 1, 0.4]
                  }}
                  transition={{
                    duration: 3.2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.5
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="relative py-20 px-6 overflow-hidden w-full bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="container mx-auto max-w-7xl w-full">
          {/* Modern Asymmetric Header */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-6">
              <motion.span
                className="text-blue-400 font-semibold mb-4 block tracking-wider text-sm uppercase"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                FITUR TRADING PREMIUM
              </motion.span>
              <motion.h2
                className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
              >
                Mulai Trading dengan{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500">
                  Mudah
                </span>
              </motion.h2>
            </div>
            <div className="lg:col-span-6 flex items-center">
              <motion.p
                className="text-lg md:text-xl text-gray-400 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Dapatkan akses ke tools trading terbaik dengan harga terjangkau dan dukungan 24/7
                untuk kesuksesan Anda.
              </motion.p>
            </div>
          </div>

          {/* Modern Dynamic Grid with Varied Sizes */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 auto-rows-fr">
            {/* $10,000 Demo Account - Larger Card */}
            <motion.div
              className="group relative bg-gradient-to-br from-gray-900/90 via-cyan-900/25 to-blue-900/25 backdrop-blur-xl rounded-3xl p-8 border border-cyan-800/40 hover:border-cyan-400/50 transition-all duration-500 cursor-pointer overflow-hidden md:col-span-2 lg:col-span-1"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              {/* Background glow effect */}
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-gradient-to-br from-cyan-500/15 to-blue-500/15 rounded-full blur-3xl"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-500 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                $10,000
              </motion.h3>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors duration-300">
                Akses Akun Demo
              </h4>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                Berlatih tanpa risiko dengan akun demo $10,000. Jelajahi strategi, pahami dinamika
                pasar, dan asah keterampilan Anda sebelum berinvestasi uang sungguhan.
              </p>

              {/* Floating particle */}
              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-cyan-400/70 rounded-full"
                animate={{
                  y: [-6, 6, -6],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </motion.div>

            {/* $10 Start - Standard Card */}
            <motion.div
              className="group relative bg-gradient-to-br from-gray-900/90 via-teal-900/25 to-cyan-900/25 backdrop-blur-xl rounded-3xl p-8 border border-teal-800/40 hover:border-teal-400/50 transition-all duration-500 cursor-pointer overflow-hidden"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-gradient-to-br from-teal-500/15 to-cyan-500/15 rounded-full blur-3xl"
                animate={{
                  scale: [1.1, 0.9, 1.1],
                  opacity: [0.2, 0.5, 0.2]
                }}
                transition={{
                  duration: 4.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              >
                $10
              </motion.h3>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-teal-300 transition-colors duration-300">
                Mulai Hanya dengan $10
              </h4>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                Mulai karir trading Anda dengan deposit minimal hanya $10—sempurna untuk belajar
                tanpa risiko finansial besar.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-teal-400/70 rounded-full"
                animate={{
                  y: [4, -4, 4],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.7
                }}
              />
            </motion.div>

            {/* $1 Entry */}
            <motion.div
              className="group relative bg-gradient-to-br from-gray-900/90 via-sky-900/25 to-blue-900/25 backdrop-blur-xl rounded-3xl p-8 border border-sky-800/40 hover:border-sky-400/50 transition-all duration-500 cursor-pointer overflow-hidden"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-gradient-to-br from-sky-500/15 to-blue-500/15 rounded-full blur-3xl"
                animate={{
                  scale: [0.9, 1.3, 0.9],
                  opacity: [0.3, 0.7, 0.3]
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              >
                $1
              </motion.h3>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-sky-300 transition-colors duration-300">
                Entry Rendah — Trade $1
              </h4>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                Mulai trading dengan minimal $1 per trade—ideal bagi pemula untuk berlatih dan
                membangun kepercayaan diri dengan terjangkau.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-sky-400/70 rounded-full"
                animate={{
                  y: [-4, 8, -4],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.2
                }}
              />
            </motion.div>

            {/* 24/7 Support - Spans 2 columns on large screens */}
            <motion.div
              className="group relative bg-gradient-to-br from-gray-900/90 via-blue-900/25 to-cyan-900/25 backdrop-blur-xl rounded-3xl p-8 border border-blue-800/40 hover:border-blue-400/50 transition-all duration-500 cursor-pointer overflow-hidden lg:col-span-2"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.4, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-gradient-to-br from-blue-500/15 to-cyan-500/15 rounded-full blur-3xl"
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.4, 0.8, 0.4]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.7,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.5
                }}
              >
                24/7
              </motion.h3>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-300 transition-colors duration-300">
                Dukungan 24/7
              </h4>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                Ada pertanyaan? Tim support ahli kami tersedia 24/7 melalui email atau live chat
                untuk membantu Anda kapan saja.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-blue-400/70 rounded-full"
                animate={{
                  y: [2, -6, 2],
                  opacity: [0.6, 1, 0.6]
                }}
                transition={{
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3
                }}
              />
            </motion.div>

            {/* 30+ Tools */}
            <motion.div
              className="group relative bg-gradient-to-br from-gray-900/90 via-cyan-900/25 via-teal-900/20 to-gray-900/90 backdrop-blur-xl rounded-3xl p-8 border border-cyan-800/40 hover:border-cyan-400/50 transition-all duration-500 cursor-pointer overflow-hidden"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.5, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-gradient-to-br from-cyan-500/15 to-teal-500/15 rounded-full blur-3xl"
                animate={{
                  scale: [1.2, 0.8, 1.2],
                  opacity: [0.2, 0.6, 0.2]
                }}
                transition={{
                  duration: 4.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.8
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-teal-400 to-cyan-300 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2
                }}
              >
                30+
              </motion.h3>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-cyan-300 transition-colors duration-300">
                30+ Alat Analisis Lanjutan
              </h4>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                Perkuat trading Anda dengan 30+ indikator dan alat charting lanjutan untuk membuat
                keputusan lebih cerdas berbasis data.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-cyan-400/70 rounded-full"
                animate={{
                  y: [-2, 4, -2],
                  opacity: [0.5, 1, 0.5]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.8
                }}
              />
            </motion.div>

            {/* Free Education */}
            <motion.div
              className="group relative bg-gradient-to-br from-gray-900/90 via-slate-800/30 to-blue-900/25 backdrop-blur-xl rounded-3xl p-8 border border-slate-600/50 hover:border-slate-400/60 transition-all duration-500 cursor-pointer overflow-hidden"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.6, ease: "easeOut" }}
              viewport={{ once: true }}
              whileHover={{
                y: -12,
                scale: 1.02,
                transition: { duration: 0.4, ease: "easeOut" }
              }}
            >
              <motion.div
                className="absolute top-4 right-4 w-40 h-40 bg-gradient-to-br from-slate-500/15 to-blue-500/15 rounded-full blur-3xl"
                animate={{
                  scale: [0.8, 1.4, 0.8],
                  opacity: [0.3, 0.9, 0.3]
                }}
                transition={{
                  duration: 4.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.8
                }}
              />

              <motion.h3
                className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-slate-300 via-blue-400 to-cyan-400 mb-3"
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 3.6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.8
                }}
              >
                Free
              </motion.h3>
              <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-slate-300 transition-colors duration-300">
                Edukasi Komprehensif Gratis
              </h4>
              <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                Dapatkan akses gratis ke sumber daya edukasi premium. Pelajari strategi, asah
                keterampilan, dan maksimalkan potensi trading Anda.
              </p>

              <motion.div
                className="absolute top-6 right-6 w-2 h-2 bg-slate-400/70 rounded-full"
                animate={{
                  y: [6, -2, 6],
                  opacity: [0.4, 1, 0.4]
                }}
                transition={{
                  duration: 3.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 2.2
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next-Gen Cards Section */}
      <section className="py-20 px-6 overflow-hidden relative w-full bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Premium Black Background with Gold Accent */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950"></div>

        {/* Subtle Gold Glow Effects */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-20 w-64 h-64 bg-yellow-500/5 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 right-20 w-80 h-80 bg-amber-500/3 rounded-full blur-3xl"
            animate={{
              scale: [1.1, 0.9, 1.1],
              opacity: [0.2, 0.5, 0.2]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 2
            }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-400/2 rounded-full blur-3xl"
            animate={{
              scale: [0.8, 1.3, 0.8],
              opacity: [0.1, 0.4, 0.1]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />

          {/* Floating Gold Sparkles */}
          <motion.div
            className="absolute top-32 left-1/4 w-2 h-2 bg-yellow-400/80 rounded-full"
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0.4, 1, 0.4],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-40 right-1/4 w-1.5 h-1.5 bg-amber-400/70 rounded-full"
            animate={{
              y: [15, -15, 15],
              x: [8, -8, 8],
              opacity: [0.5, 1, 0.5],
              scale: [1, 0.6, 1]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1.5
            }}
          />
          <motion.div
            className="absolute top-1/2 right-32 w-1 h-1 bg-yellow-300/60 rounded-full"
            animate={{
              y: [-8, 12, -8],
              opacity: [0.3, 1, 0.3],
              scale: [0.5, 1.5, 0.5]
            }}
            transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.8
            }}
          />
        </div>

        <div className="container mx-auto max-w-screen-xl px-6 relative z-10 w-full">
          {/* Modern Asymmetric Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left Side - Content */}
            <div className="lg:col-span-5 space-y-6 order-2 lg:order-1">
              <motion.span
                className="text-yellow-400 font-medium block tracking-wider text-sm uppercase"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                Penghargaan
              </motion.span>
              <motion.h2
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                viewport={{ once: true }}
              >
                Stockity Dipercaya oleh Para Profesional Trading!
              </motion.h2>
              <motion.p
                className="text-lg md:text-xl text-gray-400 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Dianugerahi "Platform Trading Paling Terpercaya" di Indonesia 2024 — Keamanan,
                Kecepatan, dan Inovasi yang Dapat Anda Percaya.
              </motion.p>
            </div>

            {/* Right Side - Award Display */}
            <div className="lg:col-span-7 relative h-[400px] lg:h-[500px] flex items-center justify-center order-1 lg:order-2">
              {/* Main Award Image */}
              <motion.div
                className="relative z-10"
                initial={{ opacity: 0, scale: 0.5, y: 50 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{
                  duration: 1.5,
                  delay: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
                viewport={{ once: true }}
                whileHover={{
                  scale: 1.05,
                  transition: { duration: 0.4 }
                }}
              >
                <div className="relative">
                  {/* Glow Effect Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/25 via-amber-500/20 to-yellow-400/25 rounded-full blur-3xl scale-150 animate-pulse" />

                  {/* Award Image */}
                  <img
                    src="/award.webp"
                    alt="Platform Trading Paling Terpercaya 2024"
                    className="w-[400px] h-auto relative z-10 drop-shadow-2xl"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Section */}
      <section className="py-20 px-6 relative w-full bg-gradient-to-br from-black via-gray-900 to-black">
        <div className="container mx-auto max-w-7xl w-full">
          {/* Modern Split Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            {/* Left - App Screenshots */}
            <motion.div
              className="lg:col-span-6 relative order-1"
              initial={{ opacity: 0, scale: 0.8, x: -50 }}
              whileInView={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              viewport={{ once: true }}
            >
              {/* Glow effect behind app */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-slate-500/10 via-gray-500/15 to-slate-500/10 rounded-3xl blur-3xl scale-110"
                animate={{
                  rotate: [0, 5, 0],
                  scale: [1.1, 1.2, 1.1]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

              <motion.img
                src="/trading-app.png"
                alt="Stockity Trading App"
                className="w-full max-w-[700px] mx-auto relative z-10 drop-shadow-2xl"
                whileHover={{
                  scale: 1.02,
                  transition: { duration: 0.4, ease: "easeOut" }
                }}
                animate={{
                  y: [-8, 8, -8]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />

            </motion.div>

            {/* Right - Content */}
            <motion.div
              className="lg:col-span-6 space-y-8 order-2"
              initial={{ opacity: 0, y: 40, x: 50 }}
              whileInView={{ opacity: 1, y: 0, x: 0 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="space-y-4">
                <motion.span
                  className="text-blue-400 font-semibold block tracking-wider text-sm uppercase"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  MOBILE APPLICATION
                </motion.span>
                <motion.h2
                  className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  Trading di{" "}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-400 via-gray-300 to-slate-400">
                    Mana Saja
                  </span>
                </motion.h2>
                <motion.p
                  className="text-lg md:text-xl text-gray-400 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  {t("mobileApp.description")}
                </motion.p>
              </div>

              {/* Modern Download Section */}
              <div className="flex flex-col gap-8 pt-4">
                {/* Download Buttons with enhanced animation */}
                <motion.div
                  className="flex flex-wrap gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  viewport={{ once: true }}
                >
                  <motion.a
                    href="https://stockity.id/auth?a=e4dd5e5e53f7&t=0#signup"
                    className="inline-block transform transition-all duration-300"
                    whileHover={{
                      scale: 1.08,
                      y: -3,
                      transition: { duration: 0.2, ease: "easeOut" }
                    }}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      y: [-1, 1, -1]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-gray-500/10 to-slate-500/10 rounded-xl blur-xl" />
                      <img
                        src="/appstore.svg"
                        alt="Download on App Store"
                        className="h-[52px] relative z-10 drop-shadow-lg"
                      />
                    </div>
                  </motion.a>
                  <motion.a
                    href="https://stockity.id/auth?a=e4dd5e5e53f7&t=0#signup"
                    className="inline-block transform transition-all duration-300"
                    whileHover={{
                      scale: 1.08,
                      y: -3,
                      transition: { duration: 0.2, ease: "easeOut" }
                    }}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      y: [1, -1, 1]
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5
                    }}
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-slate-500/10 to-gray-500/10 rounded-xl blur-xl" />
                      <img
                        src="/google-play.svg"
                        alt="Get it on Google Play"
                        className="h-[52px] relative z-10 drop-shadow-lg"
                      />
                    </div>
                  </motion.a>
                </motion.div>

                {/* QR Code Section */}
                <motion.div
                  className="flex flex-col gap-2"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                  viewport={{ once: true }}
                >
                  <div className="relative">
                    {/* Glow effect behind QR */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-gray-500/20 to-slate-500/20 rounded-2xl blur-2xl scale-110"
                      animate={{
                        opacity: [0.3, 0.6, 0.3]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />

                    <motion.img
                      src="/qrcode.png"
                      alt="Download Android APK"
                      className="w-36 mb-4 relative z-10 rounded-2xl border border-gray-600/20 p-2 bg-white/5 backdrop-blur-sm"
                      whileHover={{
                        scale: 1.05,
                        transition: { duration: 0.3 }
                      }}
                      animate={{
                        rotate: [0, 2, 0]
                      }}
                      transition={{
                        duration: 6,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </div>
                  <span className="text-gray-400 text-sm font-medium">
                    {t("mobileApp.downloadAndroid")}
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hero;
